import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
    ExternalLink, Download, Copy, Check, Lock, Palette, 
    Globe, Instagram, Linkedin, Youtube, Facebook, Twitter,
    Sun, Moon, Monitor, Link2, Calendar, BarChart3, Folder
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ContentPlanCalendar from '../components/ContentPlanCalendar';
import ReportsSection from '../components/ReportsSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// TikTok Icon
const TikTokIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
    </svg>
);

// Tab configuration
const tabs = [
    { id: 'ci', label: 'Corporate Identity', icon: Palette, color: 'from-pink-500 to-purple-500' },
    { id: 'links', label: 'Links', icon: Link2, color: 'from-blue-500 to-cyan-500' },
    { id: 'content', label: 'Content-Plan', icon: Calendar, color: 'from-emerald-500 to-teal-500' },
    { id: 'reports', label: 'Reportings', icon: BarChart3, color: 'from-violet-500 to-purple-500' }
];

const CustomerPortalPage = () => {
    const { customerId } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [passwordRequired, setPasswordRequired] = useState(false);
    const [password, setPassword] = useState('');
    const [copiedColor, setCopiedColor] = useState(null);
    const [contentPosts, setContentPosts] = useState([]);
    const [activeTab, setActiveTab] = useState('ci');
    const [theme, setTheme] = useState(() => {
        const saved = localStorage.getItem('customer-portal-theme');
        if (saved) return saved;
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    });

    // Apply theme
    useEffect(() => {
        const root = document.documentElement;
        if (theme === 'system') {
            const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            root.classList.toggle('dark', systemDark);
        } else {
            root.classList.toggle('dark', theme === 'dark');
        }
        localStorage.setItem('customer-portal-theme', theme);
    }, [theme]);

    useEffect(() => {
        fetchData();
    }, [customerId]);

    const fetchData = async (pwd) => {
        try {
            setLoading(true);
            const params = pwd ? `?password=${encodeURIComponent(pwd)}` : '';
            const response = await axios.get(`${API_URL}/api/public/customer/${customerId}${params}`);
            
            if (response.data.password_required) {
                setPasswordRequired(true);
                setData({ customer_name: response.data.customer_name });
            } else {
                setData(response.data);
                setPasswordRequired(false);
                document.title = `${response.data.customer.name} Dashboard`;
                if (response.data.settings?.show_content_plan) {
                    fetchContentPosts(pwd);
                }
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Seite nicht gefunden');
        } finally {
            setLoading(false);
        }
    };

    const fetchContentPosts = useCallback(async (pwd) => {
        try {
            const params = pwd ? `?password=${encodeURIComponent(pwd)}` : '';
            const response = await axios.get(`${API_URL}/api/public/customer/${customerId}/content-plan${params}`);
            setContentPosts(response.data);
        } catch (err) {
            console.error('Error fetching content plan:', err);
        }
    }, [customerId]);

    const handlePasswordSubmit = (e) => {
        e.preventDefault();
        fetchData(password);
    };

    const copyColor = (color) => {
        navigator.clipboard.writeText(color);
        setCopiedColor(color);
        setTimeout(() => setCopiedColor(null), 2000);
        toast.success('Farbe kopiert!');
    };

    const getLogoUrl = (logo) => {
        if (!logo) return null;
        if (typeof logo === 'string') {
            // Only return if it's a valid URL (not local path like /uploads/)
            if (logo.startsWith('http://') || logo.startsWith('https://')) {
                return logo;
            }
            return null; // Skip local paths
        }
        return logo.url || null;
    };

    // Get the first valid logo from the logos array
    const getFirstValidLogo = () => {
        if (customer.logo_url) return customer.logo_url;
        if (!customer.logos || customer.logos.length === 0) return null;
        
        for (const logo of customer.logos) {
            const url = getLogoUrl(logo);
            if (url) return url;
        }
        return null;
    };

    // Force download function with proper toast handling
    const handleDownload = async (url, filename) => {
        const toastId = toast.loading('Download wird vorbereitet...');
        
        try {
            // Use backend proxy for R2 URLs to force download
            const downloadUrl = `${API_URL}/api/download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename || 'logo.png')}`;
            
            // Fetch the file as blob for controlled download
            const response = await fetch(downloadUrl);
            
            if (!response.ok) {
                throw new Error('Download fehlgeschlagen');
            }
            
            // Get the blob from response
            const blob = await response.blob();
            
            // Create object URL and trigger download
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename || 'logo.png';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clean up the blob URL
            window.URL.revokeObjectURL(blobUrl);
            
            // Dismiss loading and show success
            toast.dismiss(toastId);
            toast.success('Download abgeschlossen!');
        } catch (error) {
            console.error('Download error:', error);
            toast.dismiss(toastId);
            toast.error('Download fehlgeschlagen');
            // Fallback: open in new tab
            window.open(url, '_blank');
        }
    };

    // Social media icons mapping
    const getSocialIcon = (platform) => {
        const icons = {
            instagram: Instagram,
            linkedin: Linkedin,
            youtube: Youtube,
            facebook: Facebook,
            twitter: Twitter,
            tiktok: TikTokIcon,
            website: Globe
        };
        return icons[platform?.toLowerCase()] || Globe;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex items-center justify-center">
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full"
                />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex items-center justify-center p-4">
                <Card className="max-w-md w-full shadow-lg">
                    <CardContent className="p-8 text-center">
                        <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                            <Lock className="w-8 h-8 text-red-500" />
                        </div>
                        <h1 className="text-xl font-bold mb-2 dark:text-white">Seite nicht verfügbar</h1>
                        <p className="text-muted-foreground">{error}</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (passwordRequired) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex items-center justify-center p-4">
                <Card className="max-w-md w-full shadow-lg">
                    <CardContent className="p-8">
                        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                            <Lock className="w-8 h-8 text-primary" />
                        </div>
                        <h1 className="text-xl font-bold text-center mb-2 dark:text-white">{data?.customer_name || 'Geschützter Bereich'}</h1>
                        <p className="text-muted-foreground text-center mb-6">Diese Seite ist passwortgeschützt</p>
                        <form onSubmit={handlePasswordSubmit} className="space-y-4">
                            <Input
                                type="password"
                                placeholder="Passwort eingeben"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="text-center"
                            />
                            <Button type="submit" className="w-full">Zugang anfordern</Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (!data) return null;

    const { customer, settings, links, agency } = data;
    const primaryColor = customer.ci_colors?.[0] || '#009dde';

    // Determine which tabs to show
    const visibleTabs = tabs.filter(tab => {
        if (tab.id === 'ci') return customer.ci_colors?.length > 0 || customer.logos?.length > 0;
        if (tab.id === 'links') return settings.show_links_section && links?.length > 0;
        if (tab.id === 'content') return settings.show_content_plan && contentPosts?.length > 0;
        if (tab.id === 'reports') return settings.show_reports;
        return false;
    });

    // Set default tab to first visible
    if (visibleTabs.length > 0 && !visibleTabs.find(t => t.id === activeTab)) {
        setActiveTab(visibleTabs[0].id);
    }

    return (
        <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 transition-colors duration-300">
            {/* Header */}
            <header 
                className="relative overflow-hidden"
                style={{
                    background: settings.header_background_url 
                        ? `url(${settings.header_background_url}) center/cover`
                        : `linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}05 100%)`
                }}
            >
                {/* Top Bar - Theme Toggle + Agency Badge */}
                <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
                    <div className="flex items-center gap-1 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full p-1 shadow-sm">
                        <button
                            onClick={() => setTheme('light')}
                            className={`p-2 rounded-full transition-all ${theme === 'light' ? 'bg-primary text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-300'}`}
                            title="Hell"
                        >
                            <Sun className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setTheme('dark')}
                            className={`p-2 rounded-full transition-all ${theme === 'dark' ? 'bg-primary text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-300'}`}
                            title="Dunkel"
                        >
                            <Moon className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setTheme('system')}
                            className={`p-2 rounded-full transition-all ${theme === 'system' ? 'bg-primary text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-300'}`}
                            title="System"
                        >
                            <Monitor className="w-4 h-4" />
                        </button>
                    </div>
                    
                    {agency?.logo_url && (
                        <div className="flex items-center gap-2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full px-3 py-1.5 shadow-sm">
                            <img src={agency.logo_url} alt={agency.name} className="w-5 h-5 object-contain" />
                            <span className="text-xs text-muted-foreground">{agency.name}</span>
                        </div>
                    )}
                </div>

                <div className="max-w-5xl mx-auto px-6 py-12 md:py-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex flex-col md:flex-row items-center gap-6"
                    >
                        {/* Logo - Use logo_url first, then fall back to logos array */}
                        {getFirstValidLogo() && (
                            <motion.div
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                className="w-24 h-24 md:w-28 md:h-28 rounded-2xl bg-white dark:bg-gray-800 shadow-lg flex items-center justify-center p-4"
                            >
                                <img 
                                    src={getFirstValidLogo()} 
                                    alt={customer.name} 
                                    className="w-full h-full object-contain"
                                />
                            </motion.div>
                        )}
                        
                        {/* Info */}
                        <div className="text-center md:text-left">
                            <h1 className="text-3xl md:text-4xl font-bold dark:text-white">{customer.name}</h1>
                            <p className="text-muted-foreground mt-1">Dashboard</p>
                            
                            {/* Social Media */}
                            {customer.social_media && Object.keys(customer.social_media).some(k => customer.social_media[k]) && (
                                <div className="flex items-center gap-2 mt-4 justify-center md:justify-start">
                                    {Object.entries(customer.social_media).map(([platform, url]) => {
                                        if (!url) return null;
                                        const Icon = getSocialIcon(platform);
                                        return (
                                            <motion.a
                                                key={platform}
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                whileHover={{ scale: 1.1 }}
                                                className="w-10 h-10 rounded-full bg-white/80 dark:bg-gray-700/80 shadow-sm flex items-center justify-center hover:bg-white dark:hover:bg-gray-700 transition-colors"
                                            >
                                                <Icon className="w-5 h-5 text-muted-foreground" />
                                            </motion.a>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </motion.div>
                </div>
            </header>

            {/* Tab Navigation - Folder Style */}
            {visibleTabs.length > 0 && (
                <div className="sticky top-0 z-20 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-border/50 dark:border-gray-800">
                    <div className="max-w-5xl mx-auto px-6">
                        <div className="flex gap-1 overflow-x-auto py-2 -mb-px">
                            {visibleTabs.map((tab) => {
                                const Icon = tab.icon;
                                const isActive = activeTab === tab.id;
                                
                                return (
                                    <motion.button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        whileHover={{ y: -2 }}
                                        whileTap={{ scale: 0.98 }}
                                        className={`
                                            flex items-center gap-2 px-5 py-3 rounded-t-xl font-medium text-sm
                                            transition-all duration-200 whitespace-nowrap
                                            ${isActive 
                                                ? 'bg-white dark:bg-gray-800 text-foreground dark:text-white shadow-lg border border-b-0 border-border/50 dark:border-gray-700' 
                                                : 'text-muted-foreground hover:text-foreground dark:hover:text-white hover:bg-white/50 dark:hover:bg-gray-800/50'
                                            }
                                        `}
                                    >
                                        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${tab.color} flex items-center justify-center`}>
                                            <Icon className="w-4 h-4 text-white" />
                                        </div>
                                        <span>{tab.label}</span>
                                    </motion.button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Main Content - flex-1 to push footer down */}
            <main className="flex-1 max-w-5xl mx-auto px-6 py-8 w-full">
                <AnimatePresence mode="wait">
                    {/* Corporate Identity Tab */}
                    {activeTab === 'ci' && (
                        <motion.div
                            key="ci"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.2 }}
                            className="space-y-8"
                        >
                            {/* Colors */}
                            {customer.ci_colors?.length > 0 && (
                                <Card className="shadow-soft dark:bg-gray-800/50 dark:border-gray-700">
                                    <CardContent className="p-6">
                                        <h3 className="font-semibold mb-4 dark:text-white">Farbpalette</h3>
                                        <div className="flex flex-wrap gap-4">
                                            {customer.ci_colors.map((color, index) => (
                                                <motion.button
                                                    key={index}
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    onClick={() => copyColor(color)}
                                                    className="group relative"
                                                >
                                                    <div 
                                                        className="w-20 h-20 rounded-xl shadow-lg transition-shadow group-hover:shadow-xl"
                                                        style={{ backgroundColor: color }}
                                                    />
                                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                        {copiedColor === color ? (
                                                            <Check className="w-6 h-6 text-white drop-shadow-lg" />
                                                        ) : (
                                                            <Copy className="w-5 h-5 text-white drop-shadow-lg" />
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-center mt-2 font-mono text-muted-foreground uppercase">
                                                        {color}
                                                    </p>
                                                </motion.button>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Logos */}
                            {customer.logos?.length > 0 && (
                                <Card className="shadow-soft dark:bg-gray-800/50 dark:border-gray-700">
                                    <CardContent className="p-6">
                                        <h3 className="font-semibold mb-4 dark:text-white">Logo Downloads</h3>
                                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {customer.logos.map((logo, index) => {
                                                const logoUrl = getLogoUrl(logo);
                                                if (!logoUrl) return null;
                                                const filename = `${customer.name.replace(/\s+/g, '_')}_Logo_${index + 1}.png`;
                                                
                                                return (
                                                    <motion.div
                                                        key={index}
                                                        whileHover={{ scale: 1.02 }}
                                                        className="group relative bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6 border-2 border-dashed border-gray-200 dark:border-gray-600 hover:border-primary dark:hover:border-primary transition-colors"
                                                    >
                                                        <div className="aspect-square flex items-center justify-center mb-4">
                                                            <img 
                                                                src={logoUrl} 
                                                                alt={`Logo ${index + 1}`}
                                                                className="max-w-full max-h-full object-contain"
                                                            />
                                                        </div>
                                                        <Button
                                                            onClick={() => handleDownload(logoUrl, filename)}
                                                            className="w-full"
                                                            variant="outline"
                                                        >
                                                            <Download className="w-4 h-4 mr-2" />
                                                            Logo {index + 1} herunterladen
                                                        </Button>
                                                    </motion.div>
                                                );
                                            })}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </motion.div>
                    )}

                    {/* Links Tab */}
                    {activeTab === 'links' && (
                        <motion.div
                            key="links"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.2 }}
                        >
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {links?.map((link, index) => (
                                    <motion.a
                                        key={link.id}
                                        href={link.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.1 * index }}
                                        whileHover={{ scale: 1.02, y: -4 }}
                                        className="block"
                                    >
                                        <Card className="h-full shadow-soft hover:shadow-floating transition-all duration-300 cursor-pointer group dark:bg-gray-800/50 dark:border-gray-700">
                                            <CardContent className="p-5">
                                                <div className="flex items-start gap-4">
                                                    <div className="text-3xl">{link.emoji}</div>
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="font-semibold group-hover:text-primary transition-colors dark:text-white">
                                                            {link.name}
                                                        </h3>
                                                        {link.description && (
                                                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                                                {link.description}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </motion.a>
                                ))}
                            </div>
                        </motion.div>
                    )}

                    {/* Content Plan Tab */}
                    {activeTab === 'content' && (
                        <motion.div
                            key="content"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.2 }}
                        >
                            <ContentPlanCalendar 
                                customerId={customerId}
                                posts={contentPosts}
                                isPublic={true}
                                onRefresh={() => fetchContentPosts(password)}
                            />
                        </motion.div>
                    )}

                    {/* Reports Tab */}
                    {activeTab === 'reports' && (
                        <motion.div
                            key="reports"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.2 }}
                        >
                            <ReportsSection 
                                customerId={customerId}
                                isPublic={true}
                                password={password}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Empty State */}
                {visibleTabs.length === 0 && (
                    <div className="text-center py-16">
                        <div className="w-20 h-20 rounded-full bg-muted/50 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
                            <Globe className="w-10 h-10 text-muted-foreground" />
                        </div>
                        <h2 className="text-xl font-semibold text-muted-foreground">Willkommen</h2>
                        <p className="text-muted-foreground mt-2">Inhalte werden bald hinzugefügt.</p>
                    </div>
                )}
            </main>

            {/* Footer - Always at bottom */}
            <footer className="border-t border-border/50 dark:border-gray-800 bg-muted/20 dark:bg-gray-900/50 mt-auto">
                <div className="max-w-5xl mx-auto px-6 py-8">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        {agency?.logo_url && (
                            <div className="flex items-center gap-3">
                                <img src={agency.logo_url} alt={agency.name} className="w-8 h-8 object-contain" />
                                <span className="text-sm text-muted-foreground">
                                    Powered by {agency.name}
                                </span>
                            </div>
                        )}
                        <p className="text-sm text-muted-foreground text-center">
                            {settings.custom_footer_text || `© ${new Date().getFullYear()} ${agency?.name || 'AgencyOS'}. Alle Rechte vorbehalten.`}
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default CustomerPortalPage;
